import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Form, FormRenderProps } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import FullSizeModalSlider from '@components/FullSizeModalSlider/FullSizeModalSlider';
import { ISupplementImage } from '@interfaces/adminSupplements';
import {
  IUserEnteredSupplementDataWithComments,
  TAdminResponseType,
} from '@interfaces/supplements';
import { adminSupplementDetailsSelector } from '@store/features/adminInquiries/inquiryEntryDetails/selectors';
import {
  handleAdminSupplementMarkAsMedication,
  handleAdminSupplementMatchExisting,
  handleAdminSupplementNeedsMoreInfo,
  handleGetAdminMatchingSupplement,
} from '@store/features/adminInquiries/newSupplement/actions';
import { adminNewSupplementMatchingSupplementSelector } from '@store/features/adminInquiries/newSupplement/selectors';
import {
  removeAdminMatchingSupplement,
  setAdminPendingNewSupplement,
} from '@store/features/adminInquiries/newSupplement/slice';
import useDispatch from '@store/hooks/useDispatch';
import { EventType, useEventBus } from '@utils/eventBus';
import handleToast from '@utils/handleToast';

import { encodeHtmlText, getParsedText } from '../SupplementInformationForm/utils';

import MatchExisting from './MatchExisting/MatchExisting';
import NewSupplementConfirmationControls from './NewSupplementConfirmationControls/NewSupplementConfirmationControls';
import NewSupplementForm from './NewSupplementForm/NewSupplementForm';
import { isOnlyCommentsMode, validateComments, validateForm } from './NewSupplementForm/utils';
import NewSupplementInitialControls from './NewSupplementInitialControls/NewSupplementInitialControls';
import { TNewSupplementResponseMode } from './NewSupplement.interface';
import { convertAdminResponseTypeIntoResponseMode, getInitialValues } from './utils';

import './style.scss';

const NewSupplement: FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const eventBus = useEventBus('newSupplement');

  const [mode, setMode] = useState(TNewSupplementResponseMode.CreateNew);
  const [selectedImage, setSelectedImage] = useState<ISupplementImage | null>(null);

  const adminSupplementInquiryDetails = useSelector(adminSupplementDetailsSelector);

  const adminNewSupplementMatchingSupplement = useSelector(
    adminNewSupplementMatchingSupplementSelector,
  );

  const initialSlide = useMemo(() => {
    const index = adminSupplementInquiryDetails?.imageUrls?.findIndex(
      item => item.url === selectedImage?.url,
    );
    if (typeof index === 'number' && index >= 0) {
      return index;
    }
    return 0;
  }, [selectedImage, adminSupplementInquiryDetails?.imageUrls]);

  const initialValues = useMemo(() => {
    const values = getInitialValues(
      adminSupplementInquiryDetails,
      adminNewSupplementMatchingSupplement,
    );
    return {
      ...values,
      comments: getParsedText(values.comments),
    };
  }, [adminSupplementInquiryDetails, adminNewSupplementMatchingSupplement]);

  useEffect(() => {
    if (initialValues.adminResponseType) {
      setMode(convertAdminResponseTypeIntoResponseMode(initialValues.adminResponseType));
    }
  }, [initialValues.adminResponseType]);

  const handleSelectMatchingSupplement = useCallback(
    (supplementId: number | null) => {
      if (supplementId) dispatch(handleGetAdminMatchingSupplement(supplementId));
      else dispatch(removeAdminMatchingSupplement());
    },
    [dispatch],
  );

  const handleCloseMatchingSupplement = useCallback(() => {
    dispatch(removeAdminMatchingSupplement());
    setMode(TNewSupplementResponseMode.CreateNew);
  }, [dispatch]);

  const handleMatchExisting = useCallback(
    (supplementId: number) => {
      if (!adminSupplementInquiryDetails || !adminSupplementInquiryDetails.userEnteredSupplementId)
        return;

      return dispatch(
        handleAdminSupplementMatchExisting({
          supplementId,
          supplementInquiryId: adminSupplementInquiryDetails.supplementInquiryId,
          userEnteredSupplementId: adminSupplementInquiryDetails.userEnteredSupplementId,
        }),
      );
    },
    [dispatch, adminSupplementInquiryDetails],
  );

  const handleNeedMoreInfo = useCallback(
    (comment: string) => {
      if (!adminSupplementInquiryDetails) return;

      return dispatch(
        handleAdminSupplementNeedsMoreInfo({
          supplementInquiryId: adminSupplementInquiryDetails.supplementInquiryId,
          comment: encodeHtmlText(comment),
        }),
      );
    },
    [dispatch, adminSupplementInquiryDetails],
  );

  const closeSlider = useCallback(() => setSelectedImage(null), []);

  const handleMarkAsMedication = useCallback(
    (comment: string) => {
      if (!adminSupplementInquiryDetails) return;

      return dispatch(
        handleAdminSupplementMarkAsMedication({
          supplementInquiryId: adminSupplementInquiryDetails.supplementInquiryId,
          comment: encodeHtmlText(comment),
        }),
      );
    },
    [dispatch, adminSupplementInquiryDetails],
  );

  const handleSubmit = useCallback(
    (values: IUserEnteredSupplementDataWithComments) => {
      if (mode === TNewSupplementResponseMode.CreateNew)
        dispatch(setAdminPendingNewSupplement(values));
      if (mode === TNewSupplementResponseMode.NeedMoreInfo)
        return handleNeedMoreInfo(values.comments!);
      if (mode === TNewSupplementResponseMode.MarkAsMedication)
        return handleMarkAsMedication(values.comments!);
    },
    [mode, dispatch, handleNeedMoreInfo, handleMarkAsMedication],
  );

  useEffect(() => {
    /* clear form on initial load */
    dispatch(removeAdminMatchingSupplement());
  }, [dispatch]);

  useEffect(() => {
    eventBus.on(EventType.ADMIN_MATCH_EXISTING_SUCCESS, () =>
      handleToast('success', t('adminNewSupplement.matchedExistingSuccessfully')),
    );

    eventBus.on(EventType.ADMIN_MARKED_AS_NEEDS_MORE_INFO_SUCCESS, () =>
      handleToast('success', t('adminNewSupplement.markedAsNeedsMoreInfoSuccessfully')),
    );

    eventBus.on(EventType.ADMIN_MARKED_AS_MEDICATION_SUCCESS, () =>
      handleToast('success', t('adminNewSupplement.markedAsMedicationSuccessfully')),
    );

    return () => {
      eventBus.off([
        EventType.ADMIN_MATCH_EXISTING_SUCCESS,
        EventType.ADMIN_MARKED_AS_NEEDS_MORE_INFO_SUCCESS,
        EventType.ADMIN_MARKED_AS_MEDICATION_SUCCESS,
      ]);
    };
  }, [eventBus, t]);

  return (
    <div className="new-supplement">
      <Form
        initialValues={initialValues}
        validate={!isOnlyCommentsMode(mode) ? validateForm : validateComments}
        onSubmit={handleSubmit}
      >
        {(props: FormRenderProps<IUserEnteredSupplementDataWithComments>) => (
          <>
            <NewSupplementForm
              {...props}
              mode={mode}
              images={adminSupplementInquiryDetails?.imageUrls}
              onImageClick={setSelectedImage}
            />

            {mode === TNewSupplementResponseMode.CreateNew ? (
              <NewSupplementInitialControls
                className="new-supplement__initial-controls"
                submitFormId="admin-new-supplement-form"
                onMatchExisting={() => setMode(TNewSupplementResponseMode.MatchExisting)}
                onMarkAsMedication={() => setMode(TNewSupplementResponseMode.MarkAsMedication)}
                onNeedMoreInfo={() => setMode(TNewSupplementResponseMode.NeedMoreInfo)}
              />
            ) : mode === TNewSupplementResponseMode.MatchExisting ? (
              <MatchExisting
                className="new-supplement__match-existing"
                confirmDisabled={props.hasValidationErrors}
                onSelect={handleSelectMatchingSupplement}
                onSubmit={handleMatchExisting}
                onCancel={handleCloseMatchingSupplement}
              />
            ) : (
              <NewSupplementConfirmationControls
                confirmLabel={t('common.confirm')}
                confirmDisabled={props.hasValidationErrors}
                submitFormId="admin-new-supplement-form"
                submitting={props.submitting}
                onCancel={() => setMode(TNewSupplementResponseMode.CreateNew)}
                hideCancel={initialValues.adminResponseType === TAdminResponseType.Medication}
              />
            )}
          </>
        )}
      </Form>

      {!!selectedImage && (
        <FullSizeModalSlider
          images={adminSupplementInquiryDetails?.imageUrls || []}
          initialSlide={initialSlide}
          handleClose={closeSlider}
        />
      )}
    </div>
  );
};

export default NewSupplement;
